/**
 * AMEAÇA CRESCENTE (10.5B) — anti-imortalidade. Escala vida/dano dos inimigos comuns
 * conforme o poder do player, mas SEMPRE um pouco atrás (lag < 1): o player evolui e
 * fica mais forte, porém os inimigos continuam sendo ameaça (nunca vira imortal).
 *
 * O Game chama update() quando os stats do player mudam (recalcPlayer). Enemy lê os mults.
 */
import { GameConfig } from "../config/GameConfig";

export const Threat = {
  hpMult: 1, // multiplicador de vida dos inimigos
  playerDamage: 10, // 10.9DT — dano efetivo do player (referencia do TETO DE GOLPES)
  dmgMult: 1, // multiplicador de dano dos inimigos

  /** Recalcula os multiplicadores a partir do poder atual do player.
   *  10.9AO — `mit` = sustain/mitigação: REGEN (HP/s), ARMADURA e RESILIÊNCIA entram como VIDA
   *  EFETIVA. Sem isso, tank de VIP (vida+regen comprados) + tier alto virava imortal: a ameaça
   *  só via a vida bruta e batia no teto. */
  update(playerMaxHp: number, playerDamage: number, mit?: { regen?: number; armor?: number; resilience?: number }): void {
    const c = GameConfig.threat;
    this.playerDamage = Math.max(1, playerDamage); // 10.9DT
    if (!c.enabled) {
      this.hpMult = 1;
      this.dmgMult = 1;
      return;
    }
    const regen = Math.max(0, mit?.regen ?? 0);
    const armor = Math.max(0, mit?.armor ?? 0);
    const res = Math.min(0.8, Math.max(0, mit?.resilience ?? 0));
    // vida efetiva: (vida + regen×janela) ÷ mitigação — armadura reduz dano por 100/(armor+100) e
    // resiliência por (1−res), então a vida "vale" ×(1+armor/100) ÷ (1−res)
    // 10.9HL — a armadura entra como vida efetiva SÓ até onde ela mitiga de verdade: o DamageSystem
    // garante que o player toma no mínimo (1 − maxTotalReduction) de cada golpe, então acima disso
    // a armadura não reduz nada — e a Ameaça não pode cobrar por uma mitigação que não existe.
    // (Antes: L120+ com armadura 480+ dava fator 5,8–8,3× → a Ameaça batia no teto e PARAVA de
    // escalar; o player virava quase imortal contra monstro comum: 4% de vida por golpe.)
    // O teto do fator é MENOR que 1/(1−maxRed)=5 porque a defesa de CLASSE (guerreiro −35%) não entra
    // na Ameaça mas entra no piso: 5 ÷ 1,54 ≈ 3,3. Medido: sem isso o Nv120+ tomava 16%/39%/45% por golpe
    // (t1/t3/t5) contra 9%/23%/27% no Nv30–80; com 3,3 volta ao mesmo patamar.
    const armorFactor = Math.min(c.armorFactorCap, 1 + armor / 100);
    const effHp = ((playerMaxHp + regen * c.regenWindow) * armorFactor) / (1 - res);
    const hpGrowth = Math.max(0, effHp / c.baseHp - 1); // quanto o player passou da vida base
    const dmgGrowth = Math.max(0, playerDamage / c.baseDamage - 1); // quanto passou do dano base
    // + vida do player → inimigos batem mais forte (mas 70% do ritmo) = mata a imortalidade
    this.dmgMult = Math.min(c.dmgMax, 1 + hpGrowth * c.dmgLag);
    // + dano do player → inimigos mais tanky (70% do ritmo) = kills seguem exigindo esforço
    this.hpMult = Math.min(c.hpMax, 1 + dmgGrowth * c.hpLag);
  },
};
